import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getInvoiceDownloadUrl, getOngoingTransaction, sendInvoice, stopCharging } from "../services/apiService";
import CableUnlockStatus from "./charging/CableUnlockStatus";
import ChargeConnectingScreen from "./charging/ChargeConnectingScreen";
import ChargeStartFailedScreen from "./charging/ChargeStartFailedScreen";
import ChargingScreen from "./charging/ChargingScreen";
import ChargeCompletedScreen from "./charging/ChargeCompletedScreen";
import InvoiceSheet from "./payment/InvoiceSheet";
import InvoiceStatusBlock from "./payment/InvoiceStatusBlock";
import ScreenLayout from "./ui/ScreenLayout";
import Dialog from "./ui/Dialog";
import Button from "./ui/Button";
import useConnectorUnlock from "../hooks/useConnectorUnlock";
import useChargeStart from "../hooks/useChargeStart";
import useChargeAmount from "../hooks/useChargeAmount";
import useReceiptReady from "../hooks/useReceiptReady";
import { useErrorHandler } from "../utils/errorHandler";
import { getConnectorPrice } from "../utils/rateUtils";
import { buildStationPanel } from "../utils/stationPanel";
import analyticsService from "../services/analyticsService";

// Le PDF du ticket est généré par Qobilling à la fin de la recharge. Le client peut
// cliquer avant que la génération soit terminée : on retente quelques fois avant
// d'annoncer que le ticket est indisponible (QCT-541).
const TICKET_RETRY_ATTEMPTS = 3;
const TICKET_RETRY_DELAY_MS = 1500;

const SESSION_POLL_MS = 5000;
// Laisse à la borne le temps d'ouvrir la transaction avant le premier appel.
const SESSION_POLL_DELAY_MS = 2000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Les codes d'erreur peuvent arriver dans le message ou dans le corps de la réponse
// selon l'erreur : on cherche dans les deux.
const ticketErrorCodes = (err) =>
  `${err?.message || ""} ${JSON.stringify(err?.data || {})}`.toUpperCase();

// Facture pas (encore) générée côté Qobilling : cas qui justifie un retry.
const isTicketPending = (err) => {
  const codes = ticketErrorCodes(err);
  return (
    codes.includes("INVOICE_PDF_NOT_FOUND") || codes.includes("INVOICE_NOT_FOUND")
  );
};

const calculateDuration = (startDate, endDate = null) => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffMins = Math.max(Math.floor((end - start) / 60000), 0);
  return `${Math.floor(diffMins / 60)}h ${String(diffMins % 60).padStart(2, "0")}min`;
};

// meterValue est en Wh.
const convertToKWh = (meterValue) => (Number(meterValue) || 0) / 1000;

// Seuls ces champs sont acceptés par /onecc/invoice-delivery (400 sinon).
const toInvoicePayload = ({ firstName, lastName, country, street, postalCode, city }) => ({
  firstName,
  lastName,
  country,
  street,
  postalCode,
  city,
});

const ConfirmSheet = ({ open, icon, tone, title, text, confirmLabel, cancelLabel, confirmVariant, onConfirm, onClose }) => (
  <Dialog open={open} onClose={onClose} icon={icon} tone={tone} title={title} text={text}>
    <Button variant={confirmVariant} className="!h-[52px] !px-2 !text-[15px]" onClick={onConfirm}>
      {confirmLabel}
    </Button>
    <Button variant="neutral" className="!px-2" onClick={onClose}>
      {cancelLabel}
    </Button>
  </Dialog>
);

export default function StartChargingComponent() {
  const { t, i18n } = useTranslation();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, connector, evcpData, finishedTransaction, requestStart, paymentIntentId } = location.state || {};

  // Arrivée depuis le paiement : c'est à cet écran d'envoyer l'ordre de démarrage.
  // Les reprises de session (lien e-mail, re-scan) ne démarrent rien.
  const [startRequested] = useState(Boolean(requestStart));

  // L'ordre ne doit partir qu'une fois : un rechargement de la page le renverrait,
  // l'historique du navigateur conservant location.state.
  useEffect(() => {
    if (!requestStart) return;
    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, requestStart: false },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // accessToken est toujours requis. evcpData ne l'est que pour une session démarrée
  // via le parcours normal ; les reprises par lien e-mail n'en disposent pas.
  useEffect(() => {
    if (!accessToken) {
      navigate("/error", {
        state: {
          error: t("startCharging.errors.missingInfo"),
          message: t("startCharging.errors.missingInfoMessage")
        }
      });
    }
  }, [accessToken, navigate, t]);

  const [error, setError] = useState("");
  const [isDownloadingTicket, setIsDownloadingTicket] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [chargingStarted, setChargingStarted] = useState(false);
  const [chargingFinished, setChargingFinished] = useState(false);
  const [currentDuration, setCurrentDuration] = useState(calculateDuration(new Date()));
  const [finalDuration, setFinalDuration] = useState(null);
  const [energyConsumed, setEnergyConsumed] = useState(0);
  const [email, setEmail] = useState(location.state?.email || null);
  const [startTime, setStartTime] = useState(null);
  const [isStoppingCharge, setIsStoppingCharge] = useState(false);
  const [showStopSheet, setShowStopSheet] = useState(false);
  const [showUnlockSheet, setShowUnlockSheet] = useState(false);
  const transactionIdRef = useRef(0);
  const finalEnergyConsumedRef = useRef(0);

  // Facture nominative : coordonnées saisies en 1m (avant le démarrage) ou en 1d.
  // QoREST ne les accepte qu'une fois la transaction ouverte : celles de 1m
  // attendent donc le démarrage (« pending ») avant d'être envoyées.
  const [invoiceDetails, setInvoiceDetails] = useState(location.state?.invoiceDetails || null);
  const [invoiceStatus, setInvoiceStatus] = useState(location.state?.invoiceDetails ? "pending" : "none");
  const [invoiceSheetOpen, setInvoiceSheetOpen] = useState(false);

  // Borne et prise de la session : connues par le parcours de démarrage, sinon
  // lues dans la transaction en cours (reprise par lien e-mail).
  const [sessionStation, setSessionStation] = useState(evcpData?.reference || null);
  const [sessionConnectorNumber, setSessionConnectorNumber] = useState(connector?.number || null);

  const {
    status: startStatus,
    failureReason: startFailureReason,
    retry: retryStart,
  } = useChargeStart({
    enabled: startRequested,
    evcpReference: evcpData?.reference,
    connectorReference: connector?.reference,
    accessToken,
    chargeStarted: chargingStarted,
  });

  const { status: unlockStatus, unlock, reportStuck } = useConnectorUnlock({
    evcpReference: sessionStation,
    connectorNumber: sessionConnectorNumber,
    accessToken,
  });

  const amount = useChargeAmount({
    transactionId: transactionIdRef.current,
    accessToken,
    enabled: chargingFinished,
  });

  const receiptReady = useReceiptReady({
    transactionId: transactionIdRef.current,
    accessToken,
    enabled: chargingFinished,
  });

  // Suivi de la session : détecte le démarrage, l'énergie, puis la fin de recharge.
  useEffect(() => {
    if (finishedTransaction || !accessToken) return undefined;
    const timer = setTimeout(() => setIsPolling(true), SESSION_POLL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [finishedTransaction, accessToken]);

  useEffect(() => {
    if (!accessToken || !isPolling) return undefined;

    const fetchTransactionData = async () => {
      try {
        const data = await getOngoingTransaction(accessToken);
        const ongoing = data?.ongoingTransactionInfo;

        if (data?.email) setEmail(data.email);

        if (ongoing?.transactionId) {
          transactionIdRef.current = ongoing.transactionId;
        }
        if (ongoing?.refBorne) setSessionStation((current) => current || ongoing.refBorne);
        if (ongoing?.connectorNumber) {
          setSessionConnectorNumber((current) => current || ongoing.connectorNumber);
        }

        if (ongoing?.meterValue !== undefined) {
          const energy = convertToKWh(ongoing.meterValue);
          finalEnergyConsumedRef.current = energy;
          setEnergyConsumed(energy);
        }

        if (!chargingStarted && ongoing) {
          setChargingStarted(true);
          if (ongoing.start) setStartTime(ongoing.start);
          analyticsService.pushPageView("charging_in_progress", {
            station_id: evcpData?.reference,
            connector_id: connector?.reference,
          });
        }

        // Charge terminée : plus de transaction ni de session sur la borne.
        if (chargingStarted && !ongoing && !data?.ongoingCpSession) {
          setChargingFinished(true);
          setIsPolling(false);
          setIsStoppingCharge(false);
          if (startTime) setFinalDuration(calculateDuration(startTime, new Date()));

          analyticsService.pushPageView("charging_completed", { station_id: evcpData?.reference });
          analyticsService.pushEvent("charge_completed", {
            station_id: evcpData?.reference,
            connector_id: connector?.reference,
            transaction_id: transactionIdRef.current,
            energy_kwh: finalEnergyConsumedRef.current,
          });
        }
      } catch (e) {
        // Volontairement pas de setError ici : le polling se rejoue toutes les
        // 5 s et une erreur passagère laisserait une bannière définitive.
        console.error("Erreur polling:", e);
      }
    };

    fetchTransactionData();
    const interval = setInterval(fetchTransactionData, SESSION_POLL_MS);
    return () => clearInterval(interval);
  }, [accessToken, isPolling, chargingStarted, startTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Durée affichée en direct pendant la recharge.
  useEffect(() => {
    if (!chargingStarted || chargingFinished || !startTime) return undefined;
    setCurrentDuration(calculateDuration(startTime));
    const interval = setInterval(() => setCurrentDuration(calculateDuration(startTime)), 1000);
    return () => clearInterval(interval);
  }, [chargingStarted, chargingFinished, startTime]);

  // Reprise d'une session déjà terminée (re-scan QR / lien email) :
  // on pré-remplit l'état "terminée" à partir du récap renvoyé par l'API, sans polling.
  useEffect(() => {
    if (!finishedTransaction) return;

    const { id, consumption, startDate, endDate } = finishedTransaction;
    transactionIdRef.current = id;
    finalEnergyConsumedRef.current = consumption != null ? convertToKWh(consumption) : 0;
    setStartTime(startDate);
    setFinalDuration(calculateDuration(startDate, endDate));
    setChargingFinished(true);

    // Pas de suivi de session ici : on ne lit l'e-mail du client que pour
    // l'afficher sur le justificatif.
    getOngoingTransaction(accessToken)
      .then((data) => data?.email && setEmail(data.email))
      .catch(() => {});

    analyticsService.pushPageView("charging_completed", { station_id: evcpData?.reference });
  }, [finishedTransaction, evcpData]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveInvoice = async (details) => {
    setInvoiceDetails(details);
    setInvoiceStatus("saving");
    try {
      await sendInvoice(paymentIntentId, toInvoicePayload(details), accessToken, transactionIdRef.current);
      setInvoiceStatus("saved");
      setInvoiceSheetOpen(false);
      analyticsService.pushEvent("invoice_details_saved", { station_id: sessionStation, context: "charging" });
    } catch (err) {
      console.error("Erreur lors de l'enregistrement des coordonnées de facturation:", err);
      setInvoiceStatus("error");
    }
  };

  // Coordonnées saisies avant le démarrage : envoyées dès que la transaction existe.
  useEffect(() => {
    if (invoiceStatus === "pending" && chargingStarted && transactionIdRef.current && paymentIntentId) {
      saveInvoice(invoiceDetails);
    }
  }, [invoiceStatus, chargingStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmStopCharge = async () => {
    setShowStopSheet(false);
    setError("");
    setIsStoppingCharge(true);
    analyticsService.pushEvent("charge_stopped_by_user", {
      station_id: sessionStation,
      connector_id: connector?.reference,
      energy_kwh: finalEnergyConsumedRef.current,
    });

    try {
      await stopCharging(sessionStation, accessToken);
    } catch (err) {
      console.error("Erreur lors de l'arrêt de la charge:", err);
      setError(handleError(err));
      setIsStoppingCharge(false);
    }
  };

  const confirmUnlock = () => {
    setShowUnlockSheet(false);
    unlock();
  };

  // Erreurs fonctionnelles renvoyées par /invoice-download-url
  const getTicketErrorMessage = (err) => {
    if (isTicketPending(err)) {
      return t("startCharging.ticket.errors.notAvailableYet");
    }

    const codes = ticketErrorCodes(err);

    if (codes.includes("TRANSACTION_NOT_FOUND")) {
      return t("startCharging.ticket.errors.notFound");
    }
    if (codes.includes("INVOICE_PDF_STORAGE_UNAVAILABLE") || err?.status >= 500) {
      return t("startCharging.ticket.errors.temporarilyUnavailable");
    }

    return t("startCharging.completed.downloadFailed");
  };

  // Retry uniquement sur "facture pas encore générée" : une transaction inconnue ou
  // une panne de stockage ne se résoudront pas en 1,5 seconde.
  const fetchTicketUrl = async (transactionId) => {
    for (let attempt = 1; attempt <= TICKET_RETRY_ATTEMPTS; attempt += 1) {
      try {
        return await getInvoiceDownloadUrl(transactionId, accessToken);
      } catch (err) {
        if (!isTicketPending(err) || attempt === TICKET_RETRY_ATTEMPTS) {
          throw err;
        }
        await wait(TICKET_RETRY_DELAY_MS);
      }
    }
  };

  const handleDownloadTicket = async () => {
    if (isDownloadingTicket) return;

    const transactionId = transactionIdRef.current;
    if (!transactionId) {
      setTicketError(t("startCharging.ticket.errors.notAvailableYet"));
      return;
    }

    setTicketError("");
    setIsDownloadingTicket(true);

    // L'onglet doit être ouvert DANS le geste utilisateur : ouvert après l'await,
    // Safari iOS le bloquerait comme une pop-up. On le remplit une fois l'URL reçue.
    const ticketWindow = window.open("", "_blank");

    try {
      const { url } = await fetchTicketUrl(transactionId);

      if (!url) {
        throw new Error("Missing download url");
      }

      analyticsService.pushEvent("invoice_downloaded", {
        station_id: sessionStation,
      });

      if (ticketWindow) {
        ticketWindow.opener = null;
        ticketWindow.location.href = url;
      } else {
        // Onglet bloqué par le navigateur : on bascule sur la navigation courante.
        window.location.href = url;
      }
    } catch (err) {
      if (ticketWindow) ticketWindow.close();
      console.error("Erreur lors du téléchargement du ticket:", err);
      analyticsService.pushEvent("ticket_download_failed", {
        error_type: err?.status || "unknown",
      });
      setTicketError(getTicketErrorMessage(err));
    } finally {
      setIsDownloadingTicket(false);
    }
  };

  if (!accessToken) return null;

  const isConnecting = startRequested && !chargingStarted && !chargingFinished;
  // Panneau de la version web : la borne pendant le démarrage, la session ensuite.
  const panelFor = (variant) =>
    buildStationPanel({ t, language: i18n.language, variant, evcpData, connector });

  if (isConnecting) {
    return startStatus === "failed" ? (
      <ChargeStartFailedScreen
        station={evcpData?.reference}
        connector={connector?.reference}
        failureReason={startFailureReason}
        onRetry={retryStart}
        panel={panelFor("station")}
      />
    ) : (
      <ChargeConnectingScreen panel={panelFor("station")} />
    );
  }

  if (chargingFinished) {
    const canUnlock = Boolean(sessionStation && sessionConnectorNumber);
    const showUnlockButton = canUnlock && (unlockStatus === "idle" || unlockStatus === "sending");

    return (
      <>
        <ChargeCompletedScreen
          amount={amount}
          energyKwh={finalEnergyConsumedRef.current}
          duration={finalDuration || calculateDuration(new Date())}
          email={email}
          receiptReady={receiptReady}
          onDownloadReceipt={handleDownloadTicket}
          isDownloadingReceipt={isDownloadingTicket}
          receiptError={ticketError}
          panel={panelFor("done")}
          unlockAction={
            showUnlockButton && (
              <Button
                icon="lock-unlocked-bold"
                onClick={() => setShowUnlockSheet(true)}
                loading={unlockStatus === "sending"}
              >
                {t("startCharging.completed.unlock")}
              </Button>
            )
          }
          unlockStatus={
            <CableUnlockStatus
              status={unlockStatus}
              station={sessionStation}
              connector={connector?.reference}
              connectorNumber={sessionConnectorNumber}
              transactionId={transactionIdRef.current}
              onRetry={unlock}
              onReportStuck={reportStuck}
            />
          }
        />
        <ConfirmSheet
          open={showUnlockSheet}
          icon="lock-unlocked-linear"
          title={t("startCharging.completed.confirmUnlockTitle")}
          text={t("startCharging.completed.confirmUnlockText")}
          confirmLabel={t("startCharging.completed.confirmUnlock")}
          cancelLabel={t("startCharging.completed.cancel")}
          confirmVariant="primary"
          onConfirm={confirmUnlock}
          onClose={() => setShowUnlockSheet(false)}
        />
      </>
    );
  }

  if (chargingStarted) {
    const price = connector ? getConnectorPrice(connector, evcpData) : null;

    return (
      <>
        <ChargingScreen
          energyKwh={energyConsumed}
          duration={currentDuration}
          pricePerKwh={price}
          email={email}
          onStop={() => setShowStopSheet(true)}
          isStopping={isStoppingCharge}
          error={error}
          panel={panelFor("charging")}
        >
          {/* La facture nominative suppose la préautorisation du parcours de
              démarrage : une session reprise n'en dispose pas. */}
          {paymentIntentId && (
            <InvoiceStatusBlock
              status={invoiceSheetOpen && invoiceStatus === "error" ? "none" : invoiceStatus}
              details={invoiceDetails}
              onAdd={() => {
                setInvoiceSheetOpen(true);
                analyticsService.pushEvent("invoice_form_opened", { station_id: sessionStation, context: "charging" });
              }}
            />
          )}
        </ChargingScreen>
        <InvoiceSheet
          open={invoiceSheetOpen}
          email={email}
          initialValues={invoiceDetails}
          onSave={saveInvoice}
          onClose={() => setInvoiceSheetOpen(false)}
          saving={invoiceStatus === "saving"}
          error={invoiceStatus === "error" ? t("invoice.saveError") : ""}
        />
        <ConfirmSheet
          open={showStopSheet}
          icon="stop-circle-linear"
          tone="danger"
          title={t("startCharging.charging.confirmStopTitle")}
          text={t("startCharging.charging.confirmStopText")}
          confirmLabel={t("startCharging.charging.confirmStop")}
          cancelLabel={t("startCharging.charging.cancel")}
          confirmVariant="dangerSolid"
          onConfirm={confirmStopCharge}
          onClose={() => setShowStopSheet(false)}
        />
      </>
    );
  }

  // Session reprise : en attendant la première réponse du suivi de session.
  return (
    <ScreenLayout>
      <div className="flex-1 flex flex-col items-center justify-center gap-6" role="status">
        <div className="h-12 w-12 rounded-full border-4 border-qv-brand-100 border-t-qv-brand animate-spin" />
        <p className="text-base font-semibold">{t("loading")}</p>
      </div>
    </ScreenLayout>
  );
}
